import NumberFlow, { continuous } from '@number-flow/react'
import { memo, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import IcBaselineStar from '~icons/ic/baseline-star'
import { cn } from 'src/utils/cx'
import { type StatConfig, STATS, TESTIMONIAL_INITIALS } from '../constants'
import css from '../Landing.module.css'
import { useFadeUp } from './useFadeUp'

// Pre-compute anchor data so we avoid per-tick object lookups
const STAT_ANCHORS = STATS.map(s => ({
  anchorMs: s.anchorTs * 1000,
  anchor: s.anchor,
  growthPerMs: s.growthPerSec / 1000,
}))

function computeCurrentValue(index: number): number {
  const { anchorMs, anchor, growthPerMs } = STAT_ANCHORS[index]
  return Math.round(anchor + growthPerMs * Math.max(0, Date.now() - anchorMs))
}

const AnimatedStat = memo(function AnimatedStat({
  stat,
  index,
  visible,
  label,
}: {
  stat: StatConfig
  index: number
  visible: boolean
  label: string
}) {
  const [value, setValue] = useState(0)
  const startedRef = useRef(false)

  useEffect(() => {
    if (!visible || startedRef.current) return
    startedRef.current = true
    setValue(computeCurrentValue(index))
    const interval = setInterval(
      () => setValue(computeCurrentValue(index)),
      500,
    )
    return () => clearInterval(interval)
  }, [visible, index])

  return (
    <div className="rounded-3xl border-[1.5px] border-[--landing-pink-light] bg-[--landing-pink-bg] p-8 text-center">
      <div className="mb-1.5 flex items-center justify-center text-4xl font-black tabular-nums text-[--landing-pink-dark]">
        <NumberFlow
          value={value}
          locales="ja-JP"
          trend={1}
          plugins={[continuous]}
          transformTiming={{ duration: 500, easing: 'linear' }}
          spinTiming={{ duration: 500, easing: 'linear' }}
        />
      </div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  )
})

export default function SocialProofSection() {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const fadeRef = useFadeUp()
  const statsRef = useRef<HTMLDivElement>(null)
  const [statsVisible, setStatsVisible] = useState(false)

  // Single IntersectionObserver for the entire stats grid
  useEffect(() => {
    const el = statsRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="social-proof" className="py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div ref={fadeRef} className={cn(css.fadeUp, 'text-center')}>
          <div className="mb-2 text-sm font-bold uppercase tracking-wider text-[--landing-pink]">
            {t('social-proof.section-label')}
          </div>
          <h2 className="mb-4 text-4xl font-semibold leading-tight text-gray-900">
            <Trans
              i18nKey="blackboard:welcome.social-proof.heading"
              components={{
                highlight: <span className={css.gradientText} />,
              }}
            />
          </h2>
        </div>

        {/* Stats */}
        <div ref={statsRef} className="mb-16 mt-12 grid gap-6 md:grid-cols-3">
          {STATS.map((stat, i) => (
            <AnimatedStat
              key={i}
              stat={stat}
              index={i}
              visible={statsVisible}
              label={t(`social-proof.stats.${i}`)}
            />
          ))}
        </div>
        <p className="-mt-12 mb-16 text-left text-xs text-gray-400">
          {t('social-proof.stats-note')}
        </p>

        {/* Testimonials */}
        <div className="grid gap-5 md:grid-cols-3">
          {TESTIMONIAL_INITIALS.map((initial, i) => (
            <div
              key={i}
              className="rounded-3xl border-[1.5px] border-gray-200 bg-white p-7"
            >
              <div className="mb-3.5 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#FF83B8] to-[#FF5A9E] font-bold text-white">
                  {initial}
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    {t(`social-proof.testimonials.${i}.name`)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {t(`social-proof.testimonials.${i}.handle`)}
                  </div>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-gray-600">
                {t(`social-proof.testimonials.${i}.text`)}
              </p>
              <div className="mt-2.5 flex gap-0.5 text-yellow-400">
                {Array.from({ length: 5 }, (_, j) => (
                  <IcBaselineStar key={j} className="size-4" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
